import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X } from "lucide-react";
import { NAV_LINKS } from "../../lib/data";

export default function Navigation() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    if (open) document.body.style.overflow = "hidden";
    else document.body.style.overflow = "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  return (
    <>
      <header
        data-testid="site-navigation"
        className={`fixed top-0 left-0 w-full z-[80] transition-all duration-500 ${
          scrolled
            ? "backdrop-blur-md bg-black/50 border-b border-white/5"
            : "bg-transparent"
        }`}
      >
        <nav className="flex items-center justify-between px-6 md:px-12 h-16 md:h-20">
          <a
            href="#top"
            aria-label="На главную"
            data-testid="nav-logo"
            data-cursor="link"
            className="flex items-center gap-3 group"
          >
            <span className="rec-dot" aria-hidden />
            <span className="font-display text-2xl md:text-[26px] tracking-wider">
              Y·A
            </span>
          </a>

          <ul className="hidden md:flex items-center gap-10">
            {NAV_LINKS.map((link) => (
              <li key={link.id}>
                <a
                  href={`#${link.id}`}
                  data-cursor="link"
                  data-testid={`nav-link-${link.id}`}
                  className="link-underline font-body text-[11px] tracking-[0.28em] uppercase text-white/80 hover:text-white transition-colors"
                >
                  {link.label}
                </a>
              </li>
            ))}
          </ul>

          <a
            href="#contact"
            data-cursor="link"
            data-testid="nav-cta-desktop"
            className="hidden md:inline-flex items-center gap-2 font-body text-[11px] tracking-[0.28em] uppercase text-white border border-white/20 px-5 py-2.5 rounded-full hover:bg-white hover:text-black transition-colors"
          >
            Написать
          </a>

          <button
            onClick={() => setOpen(true)}
            aria-label="Открыть меню"
            data-testid="mobile-menu-open"
            className="md:hidden text-white p-2 -mr-2"
          >
            <Menu className="w-6 h-6" aria-hidden />
          </button>
        </nav>
      </header>

      <AnimatePresence>
        {open ? (
          <motion.div
            initial={{ y: "-100%" }}
            animate={{ y: 0 }}
            exit={{ y: "-100%" }}
            transition={{ duration: 0.55, ease: [0.76, 0, 0.24, 1] }}
            className="fixed inset-0 z-[90] bg-[#030303] md:hidden flex flex-col"
            data-testid="mobile-menu"
          >
            <div className="flex items-center justify-between px-6 h-16 border-b border-white/5">
              <span className="font-display text-2xl tracking-wider">Y·A</span>
              <button
                onClick={() => setOpen(false)}
                aria-label="Закрыть меню"
                data-testid="mobile-menu-close"
                className="text-white p-2 -mr-2"
              >
                <X className="w-6 h-6" aria-hidden />
              </button>
            </div>

            <ul className="flex flex-col justify-center flex-1 px-8 gap-8">
              {NAV_LINKS.map((link, i) => (
                <motion.li
                  key={link.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.15 + i * 0.08, duration: 0.5 }}
                >
                  <a
                    href={`#${link.id}`}
                    onClick={() => setOpen(false)}
                    data-testid={`mobile-nav-link-${link.id}`}
                    className="font-display text-5xl uppercase tracking-wider text-white block"
                  >
                    {link.label}
                  </a>
                </motion.li>
              ))}
            </ul>

            <div className="px-8 pb-10 border-t border-white/5 pt-6 flex items-center justify-between">
              <span className="font-body text-[10px] tracking-[0.28em] uppercase text-white/40">
                Kazakhstan
              </span>
              <a
                href="#contact"
                onClick={() => setOpen(false)}
                data-testid="mobile-menu-cta"
                className="font-body text-[11px] tracking-[0.28em] uppercase text-white border border-white/20 px-5 py-2.5 rounded-full"
              >
                Написать
              </a>
            </div>
          </motion.div>
        ) : null}
      </AnimatePresence>
    </>
  );
}
